import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Popconfirm,
  Typography,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FireOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// Enums matching backend
const HotEventType = {
  SALE: 'sale',
  TOURNAMENT: 'tournament',
  SEASONAL: 'seasonal',
  LIMITED_TIME: 'limited_time',
  CHALLENGE: 'challenge',
  CELEBRATION: 'celebration',
  UPDATE: 'update',
  PARTNERSHIP: 'partnership',
  COMMUNITY: 'community',
  SPECIAL: 'special',
} as const;

const HotEventStatus = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  PAUSED: 'paused',
  ENDED: 'ended',
  CANCELLED: 'cancelled',
} as const;

const HotEventPriority = {
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
  CRITICAL: 4,
} as const;

const BannerPlacement = {
  HOME_TOP: 'home_top',
  HOME_MIDDLE: 'home_middle',
  HOME_BOTTOM: 'home_bottom',
  SHOP_TOP: 'shop_top',
  SHOP_FEATURED: 'shop_featured',
  POPUP: 'popup',
  INTERSTITIAL: 'interstitial',
  LOBBY: 'lobby',
  MATCH_END: 'match_end',
  DAILY_LOGIN: 'daily_login',
} as const;

interface HotEvent {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  shortDescription?: string;
  type: (typeof HotEventType)[keyof typeof HotEventType];
  status: (typeof HotEventStatus)[keyof typeof HotEventStatus];
  priority: number;
  startsAt: string;
  endsAt: string;
  bannerImageUrl?: string;
  thumbnailUrl?: string;
  iconUrl?: string;
  backgroundImageUrl?: string;
  videoUrl?: string;
  placements?: string[];
  ctaButtonText?: string;
  ctaDeeplink?: string;
  platform?: string;
  minBuildVersion?: number;
  maxBuildVersion?: number;
  minPlayerLevel?: number;
  maxPlayerLevel?: number;
  targetCountries?: string[];
  excludedCountries?: string[];
  maxImpressionsPerUser?: number;
  sortOrder: number;
  impressionCount: number;
  clickCount: number;
  participantCount: number;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  active: 'green',
  paused: 'orange',
  ended: 'gray',
  cancelled: 'red',
};

const priorityLabels: Record<number, string> = {
  1: 'Low',
  2: 'Normal',
  3: 'High',
  4: 'Critical',
};

const typeLabels: Record<string, string> = {
  sale: 'Sale',
  tournament: 'Tournament',
  seasonal: 'Seasonal',
  limited_time: 'Limited Time',
  challenge: 'Challenge',
  celebration: 'Celebration',
  update: 'Update',
  partnership: 'Partnership',
  community: 'Community',
  special: 'Special',
};

const placementLabels: Record<string, string> = {
  home_top: 'Home Top',
  home_middle: 'Home Middle',
  home_bottom: 'Home Bottom',
  shop_top: 'Shop Top',
  shop_featured: 'Shop Featured',
  popup: 'Popup',
  interstitial: 'Interstitial',
  lobby: 'Lobby',
  match_end: 'Match End',
  daily_login: 'Daily Login',
};

const HotEventsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<HotEvent[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getHotEvents();
      // Backend returns { data: [...], meta: {...} }
      const data = res.data?.data || res.data?.items || res.data;
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch hot events', error);
      message.error('Failed to load hot events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const formValues = values as { dateRange?: [dayjs.Dayjs, dayjs.Dayjs] } & Record<
        string,
        unknown
      >;

      const { dateRange, ...otherValues } = formValues;

      const payload = {
        ...otherValues,
        startsAt: dateRange?.[0]?.toISOString(),
        endsAt: dateRange?.[1]?.toISOString(),
      };

      if (editingId) {
        await adminApi.updateHotEvent(editingId, payload);
        message.success('Hot event updated');
      } else {
        await adminApi.createHotEvent(payload);
        message.success('Hot event created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save hot event', error);
      message.error('Failed to save hot event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: HotEvent) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      dateRange: [dayjs(record.startsAt), dayjs(record.endsAt)],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteHotEvent(id);
      message.success('Hot event deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete hot event', error);
      message.error('Failed to delete hot event');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await adminApi.updateHotEvent(id, { status: HotEventStatus.ACTIVE });
      message.success('Hot event activated');
      fetchData();
    } catch (error) {
      console.error('Failed to activate', error);
      message.error('Failed to activate hot event');
    }
  };

  const handlePause = async (id: string) => {
    try {
      await adminApi.updateHotEvent(id, { status: HotEventStatus.PAUSED });
      message.success('Hot event paused');
      fetchData();
    } catch (error) {
      console.error('Failed to pause', error);
      message.error('Failed to pause hot event');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: HotEvent) => (
        <div>
          <Text strong>{name}</Text>
          {record.displayName && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.displayName}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="orange">{typeLabels[type] || type}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: number) => {
        const colors: Record<number, string> = { 1: 'default', 2: 'blue', 3: 'orange', 4: 'red' };
        return <Tag color={colors[priority]}>{priorityLabels[priority] || priority}</Tag>;
      },
    },
    {
      title: 'Period',
      key: 'period',
      render: (_: unknown, record: HotEvent) => (
        <span>
          {dayjs(record.startsAt).format('MMM D')} - {dayjs(record.endsAt).format('MMM D')}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Stats',
      key: 'stats',
      render: (_: unknown, record: HotEvent) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            👁 {record.impressionCount || 0} | 👆 {record.clickCount || 0}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: HotEvent) => (
        <Space>
          {record.status === 'active' ? (
            <Button
              icon={<PauseCircleOutlined />}
              size="small"
              onClick={() => handlePause(record.id)}
            >
              Pause
            </Button>
          ) : record.status !== 'ended' && record.status !== 'cancelled' ? (
            <Button
              icon={<PlayCircleOutlined />}
              size="small"
              type="primary"
              onClick={() => handleActivate(record.id)}
            >
              Activate
            </Button>
          ) : null}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this event?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <FireOutlined style={{ color: '#ff4d4f' }} />
            <Title level={4} style={{ margin: 0 }}>
              Hot Events
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Create Hot Event
          </Button>
        }
      >
        <Table
          dataSource={events}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Hot Event' : 'Create Hot Event'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={900}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="configVersionId"
            label="Config Version"
            extra="Leave empty to show this to every build. Pick a config version to limit it to the app versions mapped to that version."
          >
            <ConfigVersionSelect style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Internal name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="displayName" label="Display Name">
                <Input placeholder="Displayed to users" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="description" label="Description">
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shortDescription" label="Short Description">
                <Input placeholder="Banner preview text" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {Object.entries(HotEventType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {typeLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue={HotEventStatus.DRAFT}>
                <Select>
                  {Object.entries(HotEventStatus).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority" initialValue={HotEventPriority.NORMAL}>
                <Select>
                  {Object.entries(HotEventPriority).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="dateRange" label="Event Period" rules={[{ required: true }]}>
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Divider orientation="left">Visual Assets</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bannerImageUrl" label="Banner Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="thumbnailUrl" label="Thumbnail URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="iconUrl" label="Icon URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="backgroundImageUrl" label="Background Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="videoUrl" label="Video URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Placements & CTA</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="placements" label="Banner Placements">
                <Select mode="multiple" placeholder="Select placements">
                  {Object.entries(BannerPlacement).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {placementLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="ctaButtonText" label="CTA Button Text">
                <Input placeholder="e.g., Play Now" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="ctaDeeplink" label="CTA Deeplink">
                <Input placeholder="wcc4://..." />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Targeting</Divider>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="platform" label="Platform">
                <Select placeholder="All" allowClear>
                  <Select.Option value="ios">iOS</Select.Option>
                  <Select.Option value="android">Android</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="minBuildVersion" label="Min Build Version">
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 100" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="minPlayerLevel" label="Min Player Level">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="maxPlayerLevel" label="Max Player Level">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="sortOrder" label="Sort Order" initialValue={0}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxImpressionsPerUser" label="Max Impressions/User">
                <InputNumber style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default HotEventsPage;
